import React, { useState, useEffect, useRef } from "react";
import IWithdrawForm, { TWithdrawForm, IWithdrawFees } from "./IWithdrawForm";
import FundsService from "@services/Funds.service";
import { connect } from "react-redux";
import { useWithdrawForm } from "./useWithdrawForm";
import { TReducers } from "@features/reducers";
import { IWalletDetails } from "@market/interfaces/IWallet";
import { FundsDispatchContext } from "@features/context";
import { catchError } from "@features/utils/functions";
import {
  updateBankAccounts,
  updateCryptoWallets,
} from "@funds/features/action.funds";
import {
  IPrivateWallets,
  IBankAccountProperties,
} from "../WithdrawAmount/IWithdrawAmount";
import WithdrawProcess from "../WithdrawProcess/WithdrawProcess";
import { WithdrawalProcess as WithdrawalProcessProvider } from "../WithdrawProcess/useWithdrawalProcess";

import * as style from "./WithdrawFormStyle.module.scss"

export const WithdrawForm: React.SFC<IWithdrawForm> = (
  props: IWithdrawForm
) => {
  const isInitialMount = useRef(true);
  const [state, setState] = useState<TWithdrawForm>({
    step: 1,
    stepLoading: true,
  });
  const [fees, setFees] = useState<IWithdrawFees[]>([]);
  const { dispatch } = useWithdrawForm(props.userWallet, state.step, fees);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
    } else {
      dispatch({ type: "setInitial" });
    }
  }, [props.currency]);

  useEffect(() => {
    FundsService.getWithdrawFees()
      .then((res) => {
        setState((state) => ({ ...state, stepLoading: false }));
        setFees(res.data);
      })
      .catch((err) => catchError(err, true));
  }, []);

  return (
    <div id="withdrawform" className={style.withdrawForm}>
      <div className={state.stepLoading ? style.blocked : undefined}>
        <FundsDispatchContext.Provider value={dispatch}>
          <WithdrawalProcessProvider currency={props.currency}>
            <WithdrawProcess currency={props.currency} fees={fees} />
          </WithdrawalProcessProvider>
        </FundsDispatchContext.Provider>
      </div>
    </div>
  );
};

const mapStateToProps = (
  store: TReducers,
  ownProps: IWithdrawForm
): { userWallet: IWalletDetails; bankFormAddressDataAvailable: boolean } => ({
  bankFormAddressDataAvailable: store.fundsReducer.bankFormAddressDataAvailable,
  userWallet: (store.fundsReducer.supportedUserWallets || []).filter(
    (el) => el.currency === ownProps.currency
  )[0],
});

const mapDispatchToProps = (
  dispatch: Function
): Pick<IWithdrawForm, "updateCryptoWallets" | "updateBankAccounts"> => ({
  updateBankAccounts: (data: IBankAccountProperties) =>
    dispatch(updateBankAccounts(data)),
  updateCryptoWallets: (data: IPrivateWallets) =>
    dispatch(updateCryptoWallets(data)),
});

const options = {
  areStatesEqual: (next: TReducers, prev: TReducers) => {
    return (
      prev.fundsReducer.supportedUserWallets ===
        next.fundsReducer.supportedUserWallets &&
      prev.fundsReducer.bankFormAddressDataAvailable ===
        next.fundsReducer.bankFormAddressDataAvailable
    );
  },
};

export default React.memo(
  connect<
    { userWallet: IWalletDetails; bankFormAddressDataAvailable: boolean },
    Pick<IWithdrawForm, "updateCryptoWallets" | "updateBankAccounts">
  >(
    mapStateToProps,
    mapDispatchToProps,
    undefined,
    options
  )(WithdrawForm)
);
